import {
  ChangeEvent,
  FormEvent,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import Button from '@components/Button';
import {
  listBreathingRatios,
  createBreathingRatio,
  updateBreathingRatio,
  deleteBreathingRatio,
  type NewBreathingRatio,
} from '@lib/breathingRatio';
import type { BreathingRatio } from '@lib/api';

interface RatioFormState {
  name: string;
  ratio: string;
  description: string;
  active: boolean;
}

const blankForm: RatioFormState = {
  name: '',
  ratio: '',
  description: '',
  active: false,
};

const sortByName = (list: BreathingRatio[]) =>
  [...list].sort((a, b) => a.name.localeCompare(b.name));

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

function Modal({ title, onClose, children }: ModalProps) {
  const titleId = useId();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-xl rounded-2xl bg-white/95 p-6 shadow-2xl backdrop-blur"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={titleId} className="text-xl font-semibold text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <span className="sr-only">Close</span>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 6l12 12M6 18L18 6"
              />
            </svg>
          </button>
        </div>
        <div>{children}</div>
      </div>
    </div>
  );
}

function AdminBreathingRatio() {
  const [ratios, setRatios] = useState<BreathingRatio[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeRatio, setActiveRatio] = useState<BreathingRatio | null>(null);
  const [formValues, setFormValues] = useState<RatioFormState>(blankForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showFormModal, setShowFormModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadRatios() {
      try {
        setLoading(true);
        const data = await listBreathingRatios(true);
        if (!cancelled) {
          setRatios(sortByName(data));
          setError(null);
        }
      } catch (_error) {
        if (!cancelled) {
          setError('Unable to load breathing ratios. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadRatios();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTextChange =
    (field: 'name' | 'ratio' | 'description') =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleActiveChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues((prev) => ({ ...prev, active: event.target.checked }));
  };

  const openCreateModal = () => {
    setActiveRatio(null);
    setFormValues(blankForm);
    setFormError(null);
    setShowFormModal(true);
  };

  const openEditModal = (ratio: BreathingRatio) => {
    setActiveRatio(ratio);
    setFormValues({
      name: ratio.name,
      ratio: ratio.ratio,
      description: ratio.description ?? '',
      active: Boolean(ratio.active),
    });
    setFormError(null);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setIsSubmitting(false);
  };

  const submitRatio = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    const trimmedName = formValues.name.trim();
    const trimmedRatio = formValues.ratio.trim();
    const trimmedDescription = formValues.description.trim();

    if (!trimmedName) {
      setFormError('Name is required.');
      return;
    }

    if (!trimmedRatio) {
      setFormError('Breathing ratio is required.');
      return;
    }

    const payload: NewBreathingRatio = {
      name: trimmedName,
      ratio: trimmedRatio,
      description: trimmedDescription,
      active: formValues.active,
    };

    setIsSubmitting(true);

    try {
      if (activeRatio) {
        const updated = await updateBreathingRatio(activeRatio.id, payload);
        setRatios((prev) =>
          sortByName(
            prev.map((item) => (item.id === updated.id ? updated : item))
          )
        );
      } else {
        const created = await createBreathingRatio(payload);
        setRatios((prev) => sortByName([...prev, created]));
      }
      setShowFormModal(false);
      setActiveRatio(null);
      setFormValues(blankForm);
    } catch (_error) {
      setFormError('Unable to save breathing ratio. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteModal = (ratio: BreathingRatio) => {
    setActiveRatio(ratio);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setIsDeleting(false);
  };

  const handleDelete = async () => {
    if (!activeRatio) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteBreathingRatio(activeRatio.id);
      setRatios((prev) => prev.filter((item) => item.id !== activeRatio.id));
      setShowDeleteModal(false);
      setActiveRatio(null);
    } catch (_error) {
      setDeleteError('Failed to delete breathing ratio. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  function ActionsMenu(props: {
    id?: string;
    label?: string;
    items: Array<{
      key: string;
      label: string;
      onClick: () => void;
      className?: string;
      disabled?: boolean;
    }>;
  }) {
    const { label = 'Actions', items } = props;
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      function onDoc(e: MouseEvent) {
        if (!ref.current) return;
        if (e.target instanceof Node && ref.current.contains(e.target)) return;
        setOpen(false);
      }
      function onEsc(e: KeyboardEvent) {
        if (e.key === 'Escape') setOpen(false);
      }
      document.addEventListener('mousedown', onDoc);
      document.addEventListener('keydown', onEsc);
      return () => {
        document.removeEventListener('mousedown', onDoc);
        document.removeEventListener('keydown', onEsc);
      };
    }, [ref]);

    return (
      <div ref={ref as any} className="relative inline-block text-left">
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <span className="sr-only">{label}</span>
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <circle cx="12" cy="12" r="1" />
            <circle cx="19" cy="12" r="1" />
            <circle cx="5" cy="12" r="1" />
          </svg>
        </button>

        {open ? (
          <div className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl border border-gray-100 bg-white shadow-lg">
            <div className="py-2">
              {items.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  onClick={() => {
                    it.onClick();
                    setOpen(false);
                  }}
                  disabled={it.disabled}
                  className={`w-full px-4 py-2 text-left text-sm ${it.className ?? 'text-gray-700'} ${it.disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'}`}
                >
                  {it.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-5xl px-4 py-12">
        <div className="mb-6 text-center">
          <h1 className="gradient-text mb-2 text-4xl font-bold">
            Breathing Ratios (Admin)
          </h1>
          <p className="text-lg text-gray-600">
            Manage the presets available to everyone in the app.
          </p>
        </div>

        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          <div className="mb-4 flex justify-start">
            <Button
              type="button"
              onClick={openCreateModal}
              className="!bg-teal-500 !text-white shadow-md transition hover:!bg-teal-600 focus:!ring-teal-400"
            >
              + New Ratio
            </Button>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="h-28 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
                  />
                ))}
              </div>
            ) : ratios.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
                No breathing ratios yet. Create the first one to get started.
              </div>
            ) : (
              <>
                <div className="space-y-4 lg:hidden">
                  {ratios.map((ratio) => (
                    <div
                      key={ratio.id}
                      className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
                    >
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          {ratio.name}
                        </h2>
                        <div className="mt-3 grid grid-cols-1 gap-3 text-sm text-gray-700 sm:grid-cols-6">
                          <div className="sm:col-span-1">
                            <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Name
                            </span>
                            <span className="mt-1 block text-sm text-gray-900">
                              {ratio.name}
                            </span>
                          </div>
                          <div className="sm:col-span-1">
                            <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Ratio
                            </span>
                            {ratio.ratio}
                          </div>
                          <div className="sm:col-span-1">
                            <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Status
                            </span>
                            <div className="mt-1">
                              <span
                                className={
                                  `inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ` +
                                  (ratio.active
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-rose-100 text-rose-800')
                                }
                              >
                                {ratio.active ? 'Active' : 'Not active'}
                              </span>
                            </div>
                          </div>
                          <div className="sm:col-span-2">
                            <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Description
                            </span>
                            <div className="mt-1 text-sm text-gray-700">
                              {ratio.description ? (
                                <span>{ratio.description}</span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </div>
                          </div>
                          <div className="sm:col-span-1">
                            <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Updated
                            </span>
                            {new Date(ratio.updated_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-end">
                        <ActionsMenu
                          id={ratio.id}
                          items={[
                            {
                              key: 'edit',
                              label: 'Edit',
                              onClick: () => openEditModal(ratio),
                              className: '!text-indigo-700',
                            },
                            {
                              key: 'delete',
                              label: 'Delete',
                              onClick: () => openDeleteModal(ratio),
                              className: '!text-rose-700',
                            },
                          ]}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="hidden lg:block">
                  <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                    <table className="min-w-full text-left text-sm text-gray-800">
                      <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              className="flex items-center gap-2"
                              aria-label="Sort by name"
                            >
                              <span>Name</span>
                              <span className="text-xs text-gray-400">
                                {''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              className="flex items-center gap-2"
                              aria-label="Sort by ratio"
                            >
                              <span>Ratio</span>
                              <span className="text-xs text-gray-400">
                                {''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              className="flex items-center gap-2"
                              aria-label="Sort by status"
                            >
                              <span>Status</span>
                              <span className="text-xs text-gray-400">
                                {''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3">Description</th>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              className="flex items-center gap-2"
                              aria-label="Sort by updated"
                            >
                              <span>Updated</span>
                              <span className="text-xs text-gray-400">
                                {''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ratios.map((ratio) => (
                          <tr
                            key={ratio.id}
                            className="border-t border-gray-100 hover:bg-indigo-50"
                          >
                            <td className="px-4 py-4 align-top text-gray-900">
                              <div className="font-semibold">{ratio.name}</div>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {ratio.ratio}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${ratio.active ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
                              >
                                {ratio.active ? 'Active' : 'Not active'}
                              </span>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              <span className="max-w-lg text-sm text-gray-600">
                                {ratio.description ?? '—'}
                              </span>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {new Date(ratio.updated_at).toLocaleString()}
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex flex-wrap justify-end gap-3">
                                <ActionsMenu
                                  id={ratio.id}
                                  items={[
                                    {
                                      key: 'edit',
                                      label: 'Edit',
                                      onClick: () => openEditModal(ratio),
                                      className: '!text-indigo-700',
                                    },
                                    {
                                      key: 'delete',
                                      label: 'Delete',
                                      onClick: () => openDeleteModal(ratio),
                                      className: '!text-rose-700',
                                    },
                                  ]}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showFormModal && (
        <Modal
          title={
            activeRatio ? 'Edit Breathing Ratio' : 'Create Breathing Ratio'
          }
          onClose={closeFormModal}
        >
          <form onSubmit={submitRatio} className="space-y-6">
            <div>
              <label
                htmlFor="ratio-name"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Name
              </label>
              <input
                id="ratio-name"
                type="text"
                value={formValues.name}
                maxLength={80}
                onChange={handleTextChange('name')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                placeholder="e.g. Box Breathing"
              />
            </div>

            <div>
              <label
                htmlFor="ratio-value"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Breathing Ratio
              </label>
              <input
                id="ratio-value"
                type="text"
                value={formValues.ratio}
                maxLength={80}
                onChange={handleTextChange('ratio')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                placeholder="e.g. 4-4-4-4"
              />
            </div>

            <div>
              <label
                htmlFor="ratio-description"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Description
              </label>
              <textarea
                id="ratio-description"
                value={formValues.description}
                maxLength={300}
                onChange={handleTextChange('description')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-400"
                placeholder="Short description for this preset (optional)"
                rows={3}
              />
            </div>

            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formValues.active}
                onChange={handleActiveChange}
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              Mark as active (visible in presets)
            </label>

            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <Button type="button" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="!bg-teal-500 !text-white hover:!bg-teal-600 focus:!ring-teal-400"
              >
                {isSubmitting
                  ? 'Saving…'
                  : activeRatio
                    ? 'Save Changes'
                    : 'Create Ratio'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {showDeleteModal && activeRatio && (
        <Modal title="Delete Breathing Ratio" onClose={closeDeleteModal}>
          <p className="text-sm text-gray-700">
            Are you sure you want to delete{' '}
            <span className="font-semibold">{activeRatio.name}</span>? This
            action cannot be undone.
          </p>
          {deleteError && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {deleteError}
            </div>
          )}
          <div className="mt-6 flex items-center justify-end gap-3">
            <Button type="button" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default AdminBreathingRatio;
